import React, { ReactNode, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import { FaRegCalendarAlt } from 'react-icons/fa';
import { InlineFieldRow } from '../../ui/_fields/InlineFieldRow/InlineFieldRow';
import { fromDateTimeLocalValue, toDateTimeLocalValue } from './offerDateTimeFieldLogic';

interface OfferDateTimeFieldProps {
  label: ReactNode
  value: string // 'YYYY-MM-DD HH:mm:ss', a wall clock with no zone of its own attached
  onChange: (value: string) => void
  error?: string
  isClearable?: boolean
  // Label beside the control on one line instead of above it -- appearance only, same as
  // TextField/NumberField's own `inline` opt-in.
  inline?: boolean
}

// `showPicker()` is unavailable under jsdom (no layout engine) and on older browsers -- falling
// back to focus() still opens most browsers' native picker on a datetime-local input, and is a
// harmless no-op where neither does anything.
function openNativePicker(input: HTMLInputElement | null) {
  if (!input) {
    return;
  }
  if (typeof input.showPicker === 'function') {
    try {
      input.showPicker();
      return;
    } catch {
      // falls through to focus()
    }
  }
  input.focus();
}

// input[type=datetime-local] carries a plain wall-clock string by spec -- never read back through
// a Date object here, so no zone's DST rules can shift it. Matches the dashboard's ContestsModal
// pattern. Deliberately not the shared DateTimeField (ui/_fields, `new Date(value)`-based) --
// offerDateTimeCarrierRoundTrip.test.ts proves why that must be avoided.
export function OfferDateTimeField(props: OfferDateTimeFieldProps) {
  const {
    label, value, onChange, error, isClearable, inline,
  } = props;
  const accessibleLabel = typeof label === 'string' ? label : undefined;
  const inputRef = useRef<HTMLInputElement>(null);
  const [localValue, setLocalValue] = useState(() => toDateTimeLocalValue(value));
  // What `localValue` currently reflects, whichever side last changed it (an incoming prop or our
  // own commit) -- comparing against this, on every render, is what tells the two apart. This is
  // also what makes a DST-gap correction visible: the parent recomputes `value` from the
  // corrected instant, and this reconciliation re-renders the field to the true time immediately.
  const [reflectedValue, setReflectedValue] = useState(value);

  if (value !== reflectedValue) {
    setReflectedValue(value);
    setLocalValue(toDateTimeLocalValue(value));
  }

  const commit = (nextLocalValue: string) => {
    setLocalValue(nextLocalValue);
    const combined = fromDateTimeLocalValue(nextLocalValue);
    setReflectedValue(combined);
    onChange(combined);
  };

  const control = (
    <>
      <InputGroup size="sm">
        <Form.Control
          type="datetime-local"
          aria-label={accessibleLabel}
          value={localValue}
          onChange={(event) => commit(event.target.value)}
          ref={inputRef}
        />
        <InputGroup.Append>
          <Button
            variant="outline-secondary"
            aria-label={accessibleLabel && `Open ${accessibleLabel} picker`}
            onClick={() => openNativePicker(inputRef.current)}
          >
            <FaRegCalendarAlt />
          </Button>
        </InputGroup.Append>
        {isClearable && localValue && (
          <InputGroup.Append>
            <Button
              variant="outline-secondary"
              aria-label={accessibleLabel && `Clear ${accessibleLabel}`}
              onClick={() => commit('')}
            >
              &times;
            </Button>
          </InputGroup.Append>
        )}
      </InputGroup>
      {Boolean(error) && (
        <Form.Control.Feedback type="invalid" className="d-block">
          {error}
        </Form.Control.Feedback>
      )}
    </>
  );

  if (inline) {
    return <InlineFieldRow label={label}>{control}</InlineFieldRow>;
  }

  return (
    <Form.Group>
      <Form.Label>{label}</Form.Label>
      {control}
    </Form.Group>
  );
}
